import React, { useEffect } from 'react';
import { FormProvider, Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import HeaderLayout from "@/components/common/CustomLayout/HeaderLayout";
import Schedule from '@/pages/Subscription/Edit/Schedule'
import Button from "@/components/common/Button";
import Table from '@/components/common/Table';
import { HEADERS, constructTableData, SORT, renderFilterSection } from '@/pages/Subscription/Edit/Table';
import useCustomPaging from '@/hooks/useCustomPaging';
import { getRefunds } from '@/store/refund-summary/action';
import { useAppDispatch, RootState } from '@/store';
import { getSubscriptionDetail, editSubscription } from '@/store/subscription-detail/action';
import { getVenueDetail } from '@/store/venue-detail/action';
import { getBookingList } from '@/store/bookings/action';
import { getInvoices } from '@/store/invoice/action';
import { ReactComponent as VenueIcon } from '@/assets/icons/ri-map-pin-2-fill.svg'
import { ReactComponent as UserIcon } from '@/assets/icons/ri-user-heart-fill.svg'
import { ReactComponent as CalendarIcon } from '@/assets/icons/ri-calendar-2-line.svg'
import { constructTableMetaFromPaging2 } from '@/utils/paging';
import { dynamicAttr } from '@/utils/fallback';
import { convertToLocalTime, getShortDateFormat } from '@/utils';
import 'react-datepicker/dist/react-datepicker.css';
import {
  SUBSCRIPTION_STATUS
} from "@/pages/Subscription/constant";
import {GENERAL_STATUS_STYLE} from "@/constant/status";

const scheduleData = (bookingDetails: {[key:string]: string | number}[]) => {
  const summary = {}
  bookingDetails.forEach(detail => {
    if (summary[detail.day]) {
      summary[detail.day].push(detail)
    } else {
      summary[detail.day] = [{ ...detail }]
    }
  })
  return summary
}

const SubscriptionEdit = () => {
  const { id } = useParams()
  const subscriptionDetailState = useSelector((state: RootState) => state.subscriptionDetail.state)
  const venueDetailState = useSelector((state: RootState) => state.venueDetail.venue)
  const { items: bookingState, paging: bookingPaging } = useSelector((state: RootState) => state.booking.state)
  const { invoices: invoiceState, paging: invoicePaging } = useSelector((state: RootState) => state.invoice.state)
  const { refunds: refundState, paging: refundPaging } = useSelector((state: RootState) => state.refundSummary.state)
  const statusMethod = useForm()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPageFilter,,handlePageNumberChange] = useCustomPaging()
  const activeStatus = searchParams.get('status') || 'BOOKING';
  const selectedStatus = statusMethod.watch('status')
  const handleChangeStatus = (status: string) => {
    setSearchParams({
      status,
    });
  };
  const setSortingParam = (key, order) => {
    if (key) {
      return order === 'ASC' ? key : `-${key}`
    }
    return ''
  }
  const getTableContents = (type: string) => {
    const params = {
      sizeMeta: 10,
      ...dynamicAttr('pageMeta', currentPageFilter),
      ...dynamicAttr('subscriptionMeta', subscriptionDetailState.id),
      ...dynamicAttr('sortMeta', setSortingParam(searchParams.get('sortBy'), searchParams.get('order'))),
    } as {
      sizeMeta: number
      pageMeta?: string
      sortMeta?: string
      subscriptionMeta?: string
    }
    if (type === 'BOOKING') {
      dispatch(getBookingList(params))
    } else if (type === 'INVOICE') {
      dispatch(getInvoices({
        subscriptionId: params?.subscriptionMeta,
        sortBy: params?.sortMeta,
        page: params?.pageMeta,
        size: params?.sizeMeta
      }))
    } else if (type === 'REFUND') {
      dispatch(getRefunds({
        subscriptionId: params?.subscriptionMeta,
        sortBy: params?.sortMeta,
        page: params?.pageMeta,
        size: params?.sizeMeta
      }))
    }
  }

  const tableType = () : [Booking | Invoice, Paging] => {
    if (activeStatus === 'INVOICE') {
      return [invoiceState, invoicePaging]
    }
    if (activeStatus === 'REFUND') {
      return [refundState, refundPaging]
    }
    return [bookingState, bookingPaging]
  }
  useEffect(() => {
    const fetchBookingDetail = async () => {
      await dispatch(getSubscriptionDetail({ id }))
    }
    fetchBookingDetail()
  }, [])
  useEffect(() => {
    dispatch(getVenueDetail(subscriptionDetailState.venue_id))
  }, [subscriptionDetailState.venue_id])
  useEffect(() => {
    const days = {}
    statusMethod.setValue('status', subscriptionDetailState?.status)
    subscriptionDetailState?.details?.forEach(booking => {
      if (days[booking.day]) {
        days[booking.day].add(booking.venue_court_id)
      } else {
        days[booking.day] = new Set().add(booking.venue_court_id)
      }
    })
  }, [getSubscriptionDetail, subscriptionDetailState])
  useEffect(() => {
    getTableContents(activeStatus)
  }, [subscriptionDetailState.id, searchParams]);

  return (
    <div>
      <HeaderLayout
        title='Edit Subscription'
        rightComponent={
          <div className="flex p-0 gap-4">
            <FormProvider {...statusMethod}>
              <Controller
                control={statusMethod.control}
                name='status'
                render={({ field }) =>
                  <select
                    className={`color-select ${GENERAL_STATUS_STYLE[selectedStatus]}`}
                    placeholder='Select status'
                    {...field}
                  >
                    {
                      SUBSCRIPTION_STATUS.map(i =>
                        <option
                          key={i}
                          value={i}
                          className={GENERAL_STATUS_STYLE[i]}
                        >
                          {i.replace('_', ' ')}
                        </option>)
                    }
                  </select>
                }
              />
            </FormProvider>
            <Button
              type='submit'
              variant='primary'
              className='whitespace-nowrap flex gap-2 px-12 py-2 justify-center items-center text-jsm font-medium'
              form='bookingForm'
              onClick={() => {
                dispatch(editSubscription({id: subscriptionDetailState?.id, data: { status: `${statusMethod.getValues('status')}` }}))
                navigate('/dashboard/subscriptions')
              }}
            >
              Update
            </Button>
          </div>
          }
      >
        {subscriptionDetailState && (<div className='flex flex-col px-4 pt-6'>
          <div className='flex p-0 gap-6'>
            <div className='flex flex-col w-full items-start gap-5'>
              <div className='flex flex-col w-full p-0 gap-6 drop-shadow bg-white'>
                <div className='flex px-7 pt-7 gap-2 rounded-t-sm'>
                  <p className='font-bold text-lg'>Subscription Detail</p>
                  <div className='h-full border-l border-grey-line-subtle' />
                  <p className='text-lg text-grey-dark'>ID {subscriptionDetailState?.custom_id}</p>
                </div>
                <div className='flex flex-col p-7 gap-6 rounded-b-sm'>
                  <div className="flex p-0 w-full">
                    {venueDetailState?.name && <div className='flex w-full items-center p-8 gap-8 border border-grey-line-subtle'>
											<div className='text-red-primary'>
												<VenueIcon />
											</div>
											<div className='flex flex-col p-0 gap-1'>
												<p className='text-sm text-grey-dark'>Venue</p>
												<p className='font-medium text-sm'>{venueDetailState?.name}</p>
											</div>
										</div>}
                    
                    {subscriptionDetailState?.customer && <div className='flex w-full items-center p-8 gap-8 border border-grey-line-subtle'>
											<div className='text-red-primary'>
												<UserIcon />
											</div>
											<div className='flex flex-col p-0 gap-1'>
												<p
													className='text-sm text-grey-dark'>{subscriptionDetailState?.customer?.name}</p>
												<p className='font-medium text-sm'>{subscriptionDetailState?.customer?.phone_number}</p>
											</div>
										</div>}
                    {subscriptionDetailState?.start_date && <div className='flex w-full items-center p-8 gap-8 border border-grey-line-subtle'>
											<div className='text-red-primary'>
												<CalendarIcon />
											</div>
											<div className='flex flex-col p-0 gap-1'>
												<p
													className='text-xs text-grey-dark'>Start Date</p>
												<p className='font-medium text-sm'>{`${getShortDateFormat(convertToLocalTime(subscriptionDetailState?.start_date))}`}</p>
                        <p
													className='text-xs text-grey-dark pt-1'>Renew Date</p>
												<p className='font-medium text-sm'>{`${getShortDateFormat(convertToLocalTime(subscriptionDetailState?.expires_at))}`}</p>
                      </div>
										</div>}
                  </div>
                  <div className="flex flex-col w-full p-0 gap-6">
                    <div className="flex flex-col p-0 gap-4">
                      <p className="font-medium">Schedule</p>
                      {subscriptionDetailState?.details && <Schedule summary={scheduleData(subscriptionDetailState?.details)} venues={venueDetailState}/>}
                    </div>
                    <div className="w-full border-b border-grey-line-subtle"/>
                  </div>
                </div>
              </div>
              <div className='flex flex-col w-full items-start p-0 bg-white'>
                <div className='flex items-start pl-7 pt-7'>
                  <p className='font-bold text-lg'>History</p>
                </div>
                <div className='flex flex-col w-full p-6'>
                  <div className='flex flex-col justify-between w-full p-0'>
                    {renderFilterSection({
                      activeStatus,
                      onClick: handleChangeStatus,
                    })}
                  </div>
                  <div>
                    <Table
                      headers={HEADERS(activeStatus)}
                      body={constructTableData(tableType()[0])}
                      sort={SORT}
                      meta={constructTableMetaFromPaging2(tableType()[1])}
                      pageNumberHandler={handlePageNumberChange}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='flex flex-col w-2/5 p-0 gap-6 drop-shadow bg-white h-fit'>
              <div className='flex px-7 pt-7 gap-2 rounded-t-sm'>
                <p className="font-bold text-lg">
                  Meta Data
                </p>
              </div>
              <div className='flex flex-col p-7 gap-6 rounded-b-sm'>
                <div className='flex flex-col p-0'>
                  <div className='flex justify-between py-2'>
                    <p className="text-sm">Created Date</p>
                    <p className="font-medium text-sm">{`${new Date(subscriptionDetailState?.created_at).toLocaleString('id-ID', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}`}</p>
                  </div>
                  <div className='flex justify-between py-2'>
                    <p className="text-sm">Last Modified</p>
                    <p className="font-medium text-sm">{`${new Date(subscriptionDetailState?.updated_at).toLocaleString('id-ID', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}`}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>)}
      </HeaderLayout>
    </div>
  )
}

export default SubscriptionEdit
